import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, User as UserIcon, BookOpen } from 'lucide-react';
import getReservations from '../api/GetReservations';
import { setCurrentMember } from '../store/slices/membersSlice';
import { setCurrentBook } from '../store/slices/booksSlice';

const DEFAULT_COVER = '/images/default_cover_w299.jpg';

export default function ReservedBooksModal({ isOpen, onClose, onNavigate }) {
    const [reservedLoans, setReservedLoans] = useState([]);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const currentUser = useSelector((state) => state.user.currentUser);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser.token);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const { items: books } = useSelector((state) => state.books);
    const { items: members } = useSelector((state) => state.members);

    useEffect(() => {
        const fetchReservedLoans = async () => {
            if (!isOpen) return;
            setLoading(true);
            try {
                // Fetch actual reservations
                const reservations = await getReservations(API_BASE, token, { code_etablissement });
                
                let filteredRes = reservations;
                // If student, only show personal reservations
                if (currentUser.role === 'Étudiant') {
                    const myId = currentUser.id || currentUser.ID;
                    filteredRes = reservations.filter(res => (res.user_id || res.UserID) == myId);
                }

                const mappedReservations = filteredRes.map(res => {
                    const member = res.User || members.find(m => (m.ID || m.id) == (res.user_id || res.UserID));
                    const book = res.Item || books.find(b => (b.item_id || b.id) == (res.item_id || res.ItemID));

                    return {
                        reservation: res,
                        member,
                        book,
                        startDate: new Date(res.CreatedAt || res.created_at)
                    };
                }).filter(item => item.book) // Ensure we have the book info
                  .sort((a, b) => b.startDate - a.startDate);

                setReservedLoans(mappedReservations);
            } catch (error) {
                console.error("Failed to fetch reserved books:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchReservedLoans();
    }, [isOpen, API_BASE, token, code_etablissement, books, members, currentUser]);

    const handleMemberClick = (member) => {
        if (member) {
            dispatch(setCurrentMember(member));
            onClose();
            if (onNavigate) {
                onNavigate('Members');
            }
        }
    };

    const handleBookClick = (book) => {
        if (book) {
            dispatch(setCurrentBook(book));
            onClose();
            if (onNavigate) {
                onNavigate('Books');
            }
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm lg:pl-72">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
                >
                    <div className="p-6 border-b border-white/10 flex items-center justify-between bg-amber-500/10">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-500/20 text-amber-400">
                                <Calendar size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Livres Réservés</h3>
                                <p className="text-sm text-slate-400">{reservedLoans.length} réservation{reservedLoans.length > 1 ? 's' : ''} en cours</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-full">
                            <X size={24} />
                        </button>
                    </div>

                    <div className="p-6 overflow-y-auto custom-scrollbar">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-20 gap-4">
                                <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin"></div>
                                <p className="text-slate-400 animate-pulse">Chargement des réservations...</p>
                            </div>
                        ) : reservedLoans.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {reservedLoans.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="flex gap-4 bg-slate-800/20 p-4 rounded-xl border border-white/5 hover:bg-slate-800/40 hover:border-amber-500/30 transition-all group relative"
                                    >
                                        <div
                                            onClick={() => handleBookClick(item.book)}
                                            className="cursor-pointer group flex-shrink-0"
                                        >
                                            <img
                                                src={item.book?.cover || DEFAULT_COVER}
                                                alt={item.book?.title || "Livre"}
                                                className="w-16 h-24 object-cover rounded-md shadow-lg group-hover:scale-105 transition-transform"
                                                onError={(e) => {
                                                    e.currentTarget.onerror = null;
                                                    e.currentTarget.src = DEFAULT_COVER;
                                                }}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4
                                                onClick={() => handleBookClick(item.book)}
                                                className="font-bold text-white text-base truncate mb-1 cursor-pointer hover:text-amber-400 transition-colors"
                                            >
                                                {item.book?.title || "Livre inconnu"}
                                            </h4>

                                            <div
                                                onClick={() => handleMemberClick(item.member)}
                                                className="flex items-center gap-2 mb-2 p-1.5 -ml-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors w-fit"
                                            >
                                                <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                                                    {item.member?.avatar ? (
                                                        <img src={item.member.avatar} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <UserIcon size={12} className="text-slate-400" />
                                                    )}
                                                </div>
                                                <span className="text-sm text-slate-300 font-medium truncate">{item.member?.name || "Membre inconnu"}</span>
                                            </div>

                                            <div className="flex items-center gap-2 text-xs text-slate-400">
                                                <Calendar size={12} className="text-slate-500" />
                                                <span>Réservé le {item.startDate.toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-slate-800/10 rounded-2xl border border-dashed border-white/5">
                                <Calendar className="mx-auto text-slate-700 mb-4" size={48} />
                                <p className="text-slate-400 text-lg">Aucun livre n'est actuellement réservé</p>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
